import { useEffect, useMemo, useState } from 'react';
import { useApi } from '../lib/api';
import { projectDraftCampaign } from '../lib/costProjection';
import { clampMilestoneRates } from '../lib/milestoneRates';
import { t, tp } from '../lib/i18n';
import type { Campaign, CampaignReward, RewardInput } from '../lib/types';

// Convert the DB shape of a reward to the wizard/projection input shape.
function toRewardInput(r: CampaignReward): RewardInput {
  return {
    triggerType: r.trigger_type,
    triggerValue: r.trigger_value,
    triggerLuckyDrawRatio: r.trigger_lucky_draw_ratio,
    rewardType: r.reward_type,
    rewardLabel: r.reward_label,
    rewardDescription: r.reward_description,
    rewardValueEur: Number(r.reward_value_eur),
    rewardRealCostEur:
      r.reward_real_cost_eur != null ? Number(r.reward_real_cost_eur) : null,
    rewardQuantity: r.reward_quantity,
    isCumulative: r.is_cumulative,
    position: r.position,
    maxWinners: r.max_winners,
  };
}

export default function CostCalculator() {
  const api = useApi();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [rewards, setRewards] = useState<CampaignReward[]>([]);
  const [target, setTarget] = useState(1000);
  const [milestoneRates, setMilestoneRates] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);

  // Load list once.
  useEffect(() => {
    api<{ campaigns: Campaign[] }>('/api/campaigns')
      .then((r) => {
        setCampaigns(r.campaigns);
        if (r.campaigns.length > 0) setSelected(r.campaigns[0].id);
      })
      .catch((e) => setError((e as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the selected campaign + its rewards once per selection. After this,
  // every slider change is computed client-side via projectDraftCampaign — no
  // more API spam (which previously triggered CORS errors when the proxy
  // timed out under load).
  useEffect(() => {
    if (!selected) {
      setCampaign(null);
      setRewards([]);
      return;
    }
    api<{ campaign: Campaign; rewards: CampaignReward[] }>(`/api/campaigns/${selected}`)
      .then((r) => {
        setCampaign(r.campaign);
        setRewards(r.rewards);
      })
      .catch((e) => setError((e as Error).message));
  }, [api, selected]);

  // Initialise milestone rates from rewards.
  useEffect(() => {
    const milestones = rewards.filter((r) => r.trigger_type === 'referral_milestone');
    setMilestoneRates((prev) => {
      const next = { ...prev };
      for (const m of milestones) {
        const key = String(m.trigger_value ?? '');
        if (key && next[key] === undefined) next[key] = 0.1;
      }
      return next;
    });
  }, [rewards]);

  const milestones = useMemo(
    () => rewards.filter((r) => r.trigger_type === 'referral_milestone'),
    [rewards],
  );

  const estimate = useMemo(() => {
    if (!campaign || rewards.length === 0 || target <= 0) return null;
    return projectDraftCampaign(
      {
        luckyDrawRatio: campaign.lucky_draw_ratio,
        startsAt: campaign.starts_at,
        endsAt: campaign.ends_at,
      },
      rewards.map(toRewardInput),
      { targetParticipants: target, milestoneRates },
    );
  }, [campaign, rewards, target, milestoneRates]);

  if (campaigns.length === 0) {
    return (
      <div>
        <h1 style={{ marginTop: 0 }}>{t('cost.title')}</h1>
        <div className="card muted">{t('cost.empty')}</div>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>{t('cost.title')}</h1>

      {error && <div className="error">{error}</div>}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="grid-2">
          <div>
            <label>{t('cost.campaign')}</label>
            <select value={selected} onChange={(e) => setSelected(e.target.value)}>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label>{t('cost.targetSignups')}</label>
            <input
              type="number"
              min={1}
              value={target}
              onChange={(e) => setTarget(Number(e.target.value))}
            />
          </div>
        </div>

        {milestones.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem' }}>{t('cost.milestoneRates')}</h4>
            <div className="grid-3">
              {milestones.map((m) => {
                const key = String(m.trigger_value ?? '');
                const rate = milestoneRates[key] ?? 0;
                return (
                  <div key={m.id} className="card" style={{ padding: '0.75rem' }}>
                    <div className="small muted">{m.reward_label}</div>
                    <label>
                      {tp('cost.milestoneSliderLabel', {
                        n: String(m.trigger_value ?? ''),
                        pct: (rate * 100).toFixed(1),
                      })}
                    </label>
                    <input
                      type="range"
                      min={0}
                      max={50}
                      step={0.5}
                      value={rate * 100}
                      onChange={(e) =>
                        setMilestoneRates((prev) =>
                          clampMilestoneRates(
                            prev,
                            milestones.map((mm) => String(mm.trigger_value ?? '')),
                            key,
                            Number(e.target.value) / 100,
                          ),
                        )
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {estimate && (
        <>
          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3 style={{ marginTop: 0 }}>{t('cost.projection')}</h3>
            <table className="responsive-table">
              <thead>
                <tr>
                  <th>{t('cost.col.reward')}</th>
                  <th>{t('cost.col.trigger')}</th>
                  <th style={{ textAlign: 'right' }}>{t('cost.col.winners')}</th>
                  <th style={{ textAlign: 'right' }}>{t('cost.col.costPerWinner')}</th>
                  <th style={{ textAlign: 'right' }}>{t('cost.col.total')}</th>
                </tr>
              </thead>
              <tbody>
                {estimate.rewardProjections.map((p, i) => (
                  <tr key={i}>
                    <td data-label={t('cost.col.reward')}>{p.rewardLabel}</td>
                    <td data-label={t('cost.col.trigger')} className="small">
                      {p.triggerType}{p.triggerValue ? ` (${p.triggerValue})` : ''}
                    </td>
                    <td data-label={t('cost.col.winners')} style={{ textAlign: 'right' }}>
                      <span>
                        {p.projectedWinners.toFixed(1)}
                        {p.cappedByMax && (
                          <span
                            className="badge"
                            title={tp('cost.cappedTitle', { n: String(p.maxWinners ?? '?') })}
                            style={{ marginLeft: '0.5rem' }}
                          >
                            {t('cost.cap')}
                          </span>
                        )}
                      </span>
                    </td>
                    <td data-label={t('cost.col.costPerWinner')} style={{ textAlign: 'right' }}>
                      €{p.realCostEur.toFixed(2)}
                    </td>
                    <td data-label={t('cost.col.total')} style={{ textAlign: 'right' }}>
                      €{p.projectedCost.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan={4} style={{ textAlign: 'right' }}>{t('cost.totalRow')}</th>
                  <th style={{ textAlign: 'right' }}>
                    €{estimate.summary.totalProjectedCost.toFixed(2)}
                  </th>
                </tr>
                <tr>
                  <th colSpan={4} style={{ textAlign: 'right' }}>{t('cost.perSignupRow')}</th>
                  <th style={{ textAlign: 'right' }}>
                    €{estimate.summary.costPerParticipant.toFixed(2)}
                  </th>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="notice small" style={{ marginBottom: '0.5rem' }}>
            {t('cost.scenariosExplainer')}
          </div>

          <div className="grid-3">
            {(['conservative', 'expected', 'optimistic'] as const).map((scenario) => {
              const s = estimate.summary.scenarios[scenario];
              const labelKey = `scenario.${scenario}`;
              const hintKey =
                scenario === 'conservative'
                  ? 'cost.scenarios.conservativeHint'
                  : scenario === 'expected'
                    ? 'cost.scenarios.expectedHint'
                    : 'cost.scenarios.optimisticHint';
              return (
                <div className="card" key={scenario}>
                  <div className="stat-label">{t(labelKey)}</div>
                  <div className="stat-value">€{s.totalCost.toFixed(2)}</div>
                  <div className="muted small">
                    {tp('scenario.signupsAndPer', {
                      n: s.participants,
                      cost: s.costPerParticipant.toFixed(2),
                    })}
                  </div>
                  <div className="muted small" style={{ marginTop: '0.4rem', fontStyle: 'italic' }}>
                    {t(hintKey)}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
